
import { supabase } from "@/integrations/supabase/client";
import type { Agent } from "@/types/agent";
import { mapAgentWithIcon } from "@/utils/agentUtils";

/**
 * Cria um novo agente no banco de dados
 */
export const createAgent = async (agentData: {
  name: string;
  preferred_api: string;
  api_key: string;
  prompt: string;
  model: string;
  description?: string;
}): Promise<Agent> => {
  console.log("Iniciando criação do agente:", agentData.name);
  
  // Determina ícone e avatar padrão
  const iconName = "Bot";
  const avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${agentData.name}`;
  const description = agentData.description || "Agente criado via interface";
  
  try {
    // Criando com abordagem otimizada e com timeout mais longo
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 40000); // 40 segundos de timeout
    
    console.log("Preparando inserção no banco de dados...");
    const { data, error } = await supabase
      .from('ai_agents')
      .insert([
        {
          name: agentData.name,
          preferred_api: agentData.preferred_api,
          api_key: agentData.api_key,
          prompt: agentData.prompt,
          description,
          icon_name: iconName,
          model: agentData.model,
          avatar
        }
      ])
      .select('*')
      .abortSignal(controller.signal);
    
    clearTimeout(timeoutId);
    
    if (error) {
      console.error("Erro ao criar agente:", error);
      throw new Error(error.message || "Falha ao criar agente");
    }
    
    if (!data || data.length === 0) {
      throw new Error("Nenhum dado retornado ao criar agente");
    }
    
    console.log("Agente criado com sucesso:", data[0]);
    
    // Usando mapAgentWithIcon para garantir que todos os campos obrigatórios estejam presentes
    return mapAgentWithIcon({
      id: data[0].id,
      name: data[0].name,
      description: data[0].description || "",
      iconName: data[0].icon_name,
      model: data[0].model,
      apiKey: data[0].api_key,
      openaiApiKey: data[0].openai_api_key || "",
      geminiApiKey: data[0].gemini_api_key || "",
      avatar: data[0].avatar,
      prompt: data[0].prompt,
      preferredApi: data[0].preferred_api
    });
  } catch (error: any) {
    console.error("Falha ao criar agente:", error);
    if (error.name === "AbortError") {
      throw new Error("Tempo limite excedido ao salvar o agente. Por favor, tente novamente.");
    }
    throw error;
  }
};

/**
 * Carrega a lista de agentes
 */
export const loadAgents = async (): Promise<Agent[]> => {
  console.log("Iniciando carregamento dos agentes");
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos para carregamento
    
    const { data, error } = await supabase
      .from('ai_agents')
      .select('*')
      .order('created_at', { ascending: false })
      .abortSignal(controller.signal);
    
    clearTimeout(timeoutId);

    if (error) {
      console.error("Erro ao carregar agentes:", error);
      throw error;
    }

    console.log(`Carregados ${data?.length || 0} agentes com sucesso`);

    // Mapear todos os agentes com o ícone correto para garantir que o objeto Agent seja completo
    return data.map(agent => mapAgentWithIcon({
      id: agent.id,
      name: agent.name,
      description: agent.description || "",
      iconName: agent.icon_name,
      model: agent.model,
      apiKey: agent.api_key,
      openaiApiKey: agent.openai_api_key || "",
      geminiApiKey: agent.gemini_api_key || "",
      avatar: agent.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${agent.name}`,
      prompt: agent.prompt,
      preferredApi: agent.preferred_api
    }));
  } catch (error: any) {
    console.error('Erro ao carregar agentes:', error);
    if (error.name === "AbortError") {
      throw new Error("Tempo limite excedido ao carregar os agentes. Por favor, tente novamente.");
    }
    throw error;
  }
};
